import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Consentimiento() {
  const { token } = useParams();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState(false);
  const [form, setForm] = useState({
    acepta_terminos: false, acepta_imagenes: false, acepta_comunicaciones: false,
    nombre: "", apellidos: "", telefono: "", nif: "", direccion: "", ciudad: "",
    codigo_postal: "", password: "",
  });

  useEffect(() => {
    axios.get(`${API}/publico/consentimiento/${token}`)
      .then(({ data }) => {
        setDatos(data);
        setHecho(data.usado);
        setForm((f) => ({ ...f, ...(data.tutor || {}) }));
      })
      .catch(() => setError("Este enlace no es válido o ya ha caducado."));
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const aceptar = async () => {
    setEnviando(true);
    try {
      await axios.post(`${API}/publico/consentimiento/${token}`, form);
      setHecho(true);
      toast.success("¡Gracias! El alta ya está completa");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo registrar tu aceptación");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5 py-16">
      <Card className="w-full max-w-lg rounded-2xl p-8 border-border shadow-sm" data-testid="consentimiento-page">
        <ShieldCheck className="h-9 w-9 text-primary mb-5" />
        {error && <p className="text-sm text-destructive" data-testid="consentimiento-error">{error}</p>}
        {!error && !datos && <Loader2 className="h-5 w-5 animate-spin" />}

        {datos && hecho && (
          <div data-testid="consentimiento-hecho">
            <h1 className="font-display text-3xl font-extrabold mb-3">Todo listo</h1>
            <p className="text-sm text-muted-foreground">
              Ya has aceptado los términos para {datos.alumno}. Puedes entrar al Family Hub con la
              matrícula <b className="text-foreground">{datos.matricula}</b>.
            </p>
            <Button className="rounded-full font-semibold mt-6" asChild>
              <a href="/portal/login" data-testid="ir-al-portal-btn">Entrar en mi área de familias</a>
            </Button>
          </div>
        )}

        {datos && !hecho && (
          <div className="space-y-6">
            <div>
              <h1 className="font-display text-3xl font-extrabold leading-tight">
                Completa el alta de {datos.alumno}
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                Matrícula <b className="text-foreground">{datos.matricula}</b>
                {datos.centro ? ` · ${datos.centro}` : ""}
              </p>
            </div>
            {datos.horarios?.length > 0 && (
              <div className="rounded-xl border border-border p-4" data-testid="consentimiento-horarios">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Horario</p>
                {datos.horarios.map((h, i) => (
                  <p key={i} className="text-sm font-semibold">
                    {h.dia} · {h.hora_inicio}–{h.hora_fin}
                  </p>
                ))}
              </div>
            )}

            <div className="space-y-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Tus datos
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>Nombre</Label>
                  <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)}
                    data-testid="tutor-nombre-consent" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Apellidos</Label>
                  <Input value={form.apellidos} onChange={(e) => set("apellidos", e.target.value)}
                    data-testid="tutor-apellidos-consent" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Teléfono</Label>
                  <Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)}
                    data-testid="tutor-telefono-consent" />
                </div>
                <div className="grid gap-1.5">
                  <Label>NIF / CIF</Label>
                  <Input value={form.nif} onChange={(e) => set("nif", e.target.value)}
                    data-testid="tutor-nif-consent" />
                </div>
                <div className="grid gap-1.5 sm:col-span-2">
                  <Label>Dirección</Label>
                  <Input value={form.direccion} onChange={(e) => set("direccion", e.target.value)}
                    data-testid="tutor-direccion-consent" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Ciudad</Label>
                  <Input value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)}
                    data-testid="tutor-ciudad-consent" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Código postal</Label>
                  <Input value={form.codigo_postal} onChange={(e) => set("codigo_postal", e.target.value)}
                    data-testid="tutor-cp-consent" />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label>{datos.tiene_password ? "Nueva contraseña (opcional)" : "Crea tu contraseña del portal"}</Label>
                <PasswordInput value={form.password} placeholder="Mínimo 6 caracteres"
                  onChange={(e) => set("password", e.target.value)} data-testid="tutor-password-consent" />
                <p className="text-xs text-muted-foreground">
                  Entrarás al Family Hub con la matrícula <b>{datos.matricula}</b> y esta contraseña.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <Checkbox checked={form.acepta_terminos} data-testid="acepta-terminos-check"
                  onCheckedChange={(v) => set("acepta_terminos", !!v)} />
                <span>
                  Acepto los{" "}
                  <a href="/legal/terminos" target="_blank" rel="noreferrer" className="text-primary font-semibold underline">
                    términos del servicio</a>{" "}y la{" "}
                  <a href="/legal/privacidad" target="_blank" rel="noreferrer" className="text-primary font-semibold underline">
                    política de privacidad</a>, y confirmo que soy el padre, madre o tutor legal del menor.
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <Checkbox checked={form.acepta_imagenes} data-testid="acepta-imagenes-check"
                  onCheckedChange={(v) => set("acepta_imagenes", !!v)} />
                <span>Autorizo el uso de fotos y vídeos de sus trabajos en el aula <span className="text-muted-foreground">(opcional)</span></span>
              </label>
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <Checkbox checked={form.acepta_comunicaciones} data-testid="acepta-comunicaciones-check"
                  onCheckedChange={(v) => set("acepta_comunicaciones", !!v)} />
                <span>Quiero recibir novedades y promociones del centro <span className="text-muted-foreground">(opcional)</span></span>
              </label>
            </div>
            <Button className="rounded-full font-semibold w-full" onClick={aceptar}
              disabled={enviando || !form.acepta_terminos} data-testid="confirmar-consentimiento-btn">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Check className="h-4 w-4 mr-2" /> Guardar y completar el alta</>}
            </Button>
            <p className="text-xs text-muted-foreground">
              Puedes retirar tu consentimiento en cualquier momento desde tu perfil o escribiéndonos.
              Guardamos la fecha y la IP de esta aceptación como prueba, tal y como exige el RGPD.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
